import { LoginForm, RegisterForm } from '@/components/auth/AuthForms';
import { AuthHeroPanel } from '@/components/auth/AuthHeroPanel';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/dialog';
import { useI18n } from '@/hooks/useI18n';

export type AuthDialogMode = 'login' | 'register';

interface AuthDialogProps {
  open: boolean;
  mode: AuthDialogMode;
  redirectTo: string;
  onAuthSuccess: () => void;
  onModeChange: (mode: AuthDialogMode) => void;
  onOpenChange: (open: boolean) => void;
}

export function AuthDialog({
  open,
  mode,
  redirectTo,
  onAuthSuccess,
  onModeChange,
  onOpenChange,
}: AuthDialogProps) {
  const { t } = useI18n();
  const close = () => onOpenChange(false);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        closeLabel={t('common.close')}
        className="block max-h-[calc(100dvh-1rem)] w-[calc(100%-1rem)] max-w-md overflow-hidden rounded-2xl border-0 p-0 lg:max-h-[calc(100vh-3rem)] lg:max-w-5xl lg:rounded-3xl"
      >
        <DialogTitle className="sr-only">
          {mode === 'login' ? t('auth.login') : t('auth.register')}
        </DialogTitle>
        <DialogDescription className="sr-only">
          {mode === 'login'
            ? t('auth.loginSubtitle')
            : t('auth.registerSubtitle')}
        </DialogDescription>

        <div className="grid lg:grid-cols-[0.9fr_1.1fr]">
          <AuthHeroPanel compact onNavigateHome={close} />

          <section className="max-h-[calc(100dvh-1rem)] overflow-y-auto p-5 pb-[max(1.25rem,env(safe-area-inset-bottom))] sm:p-7 lg:max-h-[calc(100vh-3rem)] lg:p-10">
            {mode === 'login' ? (
              <LoginForm
                key="login"
                redirectTo={redirectTo}
                onSuccess={onAuthSuccess}
                onSwitchMode={() => onModeChange('register')}
                onNavigateAway={close}
                idPrefix="dialog-login-"
                headingLevel="h2"
              />
            ) : (
              <RegisterForm
                key="register"
                redirectTo={redirectTo}
                onSuccess={onAuthSuccess}
                onSwitchMode={() => onModeChange('login')}
                onNavigateAway={close}
                idPrefix="dialog-register-"
                headingLevel="h2"
              />
            )}
          </section>
        </div>
      </DialogContent>
    </Dialog>
  );
}
